import { useEscrowNotificationHandlers } from 'src/containers/Escrow/Technical';
import { SoftwareOnboardingMain } from 'src/components/SoftwareOnboarding';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import { EscrowPartyTypeEnum } from 'src/graphql/schema';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { getEscrowActivityNotificationOptions } from 'src/shared/entities/escrow-activity-notification-options';
import { disruptedNotificationsOptionsRich, gracePeriodOptions } from 'src/shared/entities/escrow-notification-options';
import {
  PartyNotificationSettings,
  ActivityNotifications,
  DisruptedAccessNotifications,
} from 'src/components/Escrow/Notifications';

const { activityNotificationOptionsRich } = getEscrowActivityNotificationOptions('software');

const NotificationSettingsPage = () => {
  const {
    softwareEscrow,
    softwareEscrow: { id: escrowId, currentCompanyRole, startedBy, beneficiarySharedInformation },
  } = useSoftwareEscrow();
  const { onActivityNotificationChange, onDisruptionNotificationChange, onGracePeriodChange } =
    useEscrowNotificationHandlers({
      escrowId,
      currentCompanyRole: currentCompanyRole!,
    });

  const isInvitedParty = currentCompanyRole !== startedBy;
  const notificationSettings = {
    activityNotification:
      currentCompanyRole === EscrowPartyTypeEnum.Depositor
        ? softwareEscrow.depositorActivityNotification
        : softwareEscrow.beneficiaryActivityNotification,
    disruptionNotification:
      currentCompanyRole === EscrowPartyTypeEnum.Depositor
        ? softwareEscrow.depositorDisruptionNotification
        : softwareEscrow.beneficiaryDisruptionNotification,
    disruptionNotificationGracePeriod:
      (currentCompanyRole === EscrowPartyTypeEnum.Depositor
        ? softwareEscrow.depositorDisruptionNotificationGracePeriod
        : softwareEscrow.beneficiaryDisruptionNotificationGracePeriod) ?? undefined,
  };

  return (
    <SoftwareOnboardingMain
      subtitle="Technical information:"
      title={`Notification settings for the ${currentCompanyRole}`}
      description={
        currentCompanyRole === EscrowPartyTypeEnum.Depositor
          ? "Manage the information and the notifications you'll receive."
          : 'Manage the notifications you’ll receive.'
      }
      noteText="You can edit this later under Settings → Technical Information."
    >
      <div className="container mx-auto grow">
        {isInvitedParty && !beneficiarySharedInformation ? (
          <Card className="bg-[--alpha(var(--muted)/60%)] w-[580px] mx-auto">
            <CardHeader>
              <CardTitle>Activity notifications</CardTitle>
              <CardDescription className="text-foreground text-xs">
                You’ll receive an escrow activation email but no other notifications or information will be shared with
                you.
                <br />
                <br /> To request asset visibility, go to Settings and submit a change request after completing
                onboarding.
              </CardDescription>
            </CardHeader>
          </Card>
        ) : (
          <PartyNotificationSettings
            activityNotificationNode={
              <ActivityNotifications
                description={`What type of activity notifications would you like to receive?\nThese may include details like file names and deposit timestamps.`}
                value={notificationSettings.activityNotification}
                options={activityNotificationOptionsRich}
                onChange={onActivityNotificationChange}
              />
            }
            disruptedAccessNotificationNode={
              <DisruptedAccessNotifications
                description="Choose whether to alert members if an asset becomes inaccessible or can no longer be deposited."
                values={{
                  disruptionNotification: notificationSettings.disruptionNotification,
                  disruptionNotificationGracePeriod: notificationSettings.disruptionNotificationGracePeriod ?? null,
                }}
                options={{
                  disruptionNotificationOptions: disruptedNotificationsOptionsRich,
                  gracePeriodOptions: gracePeriodOptions,
                }}
                onDisruptionNotificationChange={onDisruptionNotificationChange}
                onDisruptionNotificationGracePeriodChange={onGracePeriodChange}
                graceNote="We’ll keep retrying the deposit during this period before notifying you that something’s wrong."
                graceLabel="Disruption grace period"
              />
            }
          />
        )}
      </div>
    </SoftwareOnboardingMain>
  );
};

export default NotificationSettingsPage;
